import React, { useCallback, useEffect, useState, useRef } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Save, Trash2, Play, Shield, Zap, MousePointer2, Undo2, X } from "lucide-react";

const YARD_LINES = Array.from({ length: 9 }, (_, i) => 50 + i * 50);
const HASH_MARKS = Array.from({ length: 40 }, (_, i) => 20 + i * 12);

const SIDES = [
  { v: "offense", l: "Offense", icon: Play, color: "text-amber" },
  { v: "defense", l: "Defense", icon: Shield, color: "text-emerald-400" },
  { v: "special_teams", l: "Special Teams", icon: Zap, color: "text-sky-400" },
];

const SITUATIONS = [
  { v: "goal_line", l: "Goal Line" },
  { v: "short_yardage", l: "Short Yardage" },
  { v: "red_zone", l: "Red Zone" },
  { v: "two_minute", l: "2-Minute" },
  { v: "four_minute", l: "4-Minute" },
];

// -------------------- Diagram editor --------------------
// Simple SVG-based play diagram:
//   positions: [{id, label, x, y, kind}]  kind: 'O' (offense) | 'X' (defense) | 'QB' | 'RB' | 'WR' | 'TE'
//   routes:    [{id, from_id, points: [{x,y}]}]

function DiagramEditor({ value, onChange, readOnly = false }) {
  const svgRef = useRef(null);
  const [tool, setTool] = useState("select"); // select | O | X | QB | RB | WR | TE | route
  const [routeDraft, setRouteDraft] = useState(null); // {from_id, points: []}
  const [dragging, setDragging] = useState(null); // pos id being dragged

  const doc = value || { positions: [], routes: [] };

  const toSvgPoint = (evt) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const ctm = svg.getScreenCTM().inverse();
    const p = pt.matrixTransform(ctm);
    return { x: Math.round(p.x), y: Math.round(p.y) };
  };

  const handleSvgClick = (e) => {
    if (readOnly) return;
    if (dragging) return; // ignore click while dragging
    if (["O","X","QB","RB","WR","TE"].includes(tool)) {
      const { x, y } = toSvgPoint(e);
      const id = Math.random().toString(36).slice(2, 8);
      const label = tool === "O" ? "" : tool === "X" ? "" : tool;
      const kind = tool;
      onChange({ ...doc, positions: [...doc.positions, { id, label, x, y, kind }] });
    } else if (tool.startsWith("CUSTOM:")) {
      const idx = +tool.slice(7);
      const label = (doc.customLabels?.[idx] || `C${idx + 1}`).slice(0, 6).toUpperCase();
      const { x, y } = toSvgPoint(e);
      const id = Math.random().toString(36).slice(2, 8);
      onChange({ ...doc, positions: [...doc.positions, { id, label, x, y, kind: "O" }] });
    } else if (tool === "route") {
      const { x, y } = toSvgPoint(e);
      if (!routeDraft) return;
      setRouteDraft({ ...routeDraft, points: [...routeDraft.points, { x, y }] });
    }
  };

  const setCustomLabel = (idx, val) => {
    const next = [...(doc.customLabels || ["", "", ""])];
    next[idx] = val;
    onChange({ ...doc, customLabels: next });
  };

  const startRouteFrom = (posId) => {
    if (readOnly || tool !== "route") return;
    setRouteDraft({ from_id: posId, points: [] });
  };

  const finishRoute = () => {
    if (!routeDraft || routeDraft.points.length === 0) { setRouteDraft(null); return; }
    const id = Math.random().toString(36).slice(2, 8);
    onChange({ ...doc, routes: [...doc.routes, { id, ...routeDraft }] });
    setRouteDraft(null);
  };

  const removePosition = (posId) => {
    if (readOnly) return;
    onChange({
      ...doc,
      positions: doc.positions.filter(p => p.id !== posId),
      routes: doc.routes.filter(r => r.from_id !== posId),
    });
  };

  const removeRoute = (rid) => {
    if (readOnly) return;
    onChange({ ...doc, routes: doc.routes.filter(r => r.id !== rid) });
  };

  const undo = () => {
    if (doc.routes.length) return onChange({ ...doc, routes: doc.routes.slice(0, -1) });
    if (doc.positions.length) return onChange({ ...doc, positions: doc.positions.slice(0, -1) });
  };

  const clear = () => onChange({ positions: [], routes: [] });

  // Drag handlers
  const onPosMouseDown = (e, posId) => {
    e.stopPropagation();
    if (readOnly) return;
    if (tool === "route") { startRouteFrom(posId); return; }
    setDragging(posId);
  };
  const onSvgMouseMove = (e) => {
    if (!dragging) return;
    const { x, y } = toSvgPoint(e);
    onChange({
      ...doc,
      positions: doc.positions.map(p => p.id === dragging ? { ...p, x, y } : p),
    });
  };
  const onSvgMouseUp = () => setDragging(null);

  const renderPos = (p) => {
    const isOffense = p.kind === "O" || ["QB","RB","WR","TE"].includes(p.kind);
    const color = isOffense ? "#F59E0B" : "#10B981";
    return (
      <g key={p.id} onMouseDown={(e) => onPosMouseDown(e, p.id)} style={{cursor: readOnly ? "default" : (tool === "route" ? "crosshair" : "grab")}}>
        {p.kind === "X" ? (
          <>
            <line x1={p.x-5} y1={p.y-5} x2={p.x+5} y2={p.y+5} stroke={color} strokeWidth="1.8" />
            <line x1={p.x-5} y1={p.y+5} x2={p.x+5} y2={p.y-5} stroke={color} strokeWidth="1.8" />
          </>
        ) : (
          <>
            <circle cx={p.x} cy={p.y} r="6" fill="#0B0D12" stroke={color} strokeWidth="1.5" />
            <text x={p.x} y={p.y+2} textAnchor="middle" fontSize="6" fontWeight="700" fill={color} fontFamily="'Barlow Condensed', sans-serif">
              {p.label || (["QB","RB","WR","TE"].includes(p.kind) ? p.kind : "")}
            </text>
          </>
        )}
        {!readOnly && (
          <circle cx={p.x+6} cy={p.y-6} r="5" fill="#EF4444" opacity="0" style={{cursor:"pointer"}}
            onClick={(e) => { e.stopPropagation(); removePosition(p.id); }}
          />
        )}
      </g>
    );
  };

  const renderRoute = (r) => {
    const from = doc.positions.find(p => p.id === r.from_id);
    if (!from) return null;
    const pts = [{x: from.x, y: from.y}, ...r.points];
    const d = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");
    return (
      <g key={r.id} onClick={() => removeRoute(r.id)} style={{cursor: readOnly ? "default" : "pointer"}}>
        <path d={d} stroke="#FBBF24" strokeWidth="2.5" fill="none" markerEnd="url(#arrow)" />
      </g>
    );
  };

  const renderRouteDraft = () => {
    if (!routeDraft) return null;
    const from = doc.positions.find(p => p.id === routeDraft.from_id);
    if (!from) return null;
    const pts = [{x: from.x, y: from.y}, ...routeDraft.points];
    const d = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");
    return <path d={d} stroke="#F59E0B" strokeDasharray="4 3" strokeWidth="2" fill="none" />;
  };

  return (
    <div className="border border-surface-3 bg-surface" data-testid="diagram-editor">
      {!readOnly && (
        <>
        <div className="flex items-center flex-wrap gap-2 px-3 py-2 border-b border-surface-3">
          {[
            { t: "select", label: "Select", icon: MousePointer2 },
            { t: "O", label: "O (Offense)" },
            { t: "X", label: "X (Defense)" },
            { t: "QB", label: "QB" },
            { t: "RB", label: "RB" },
            { t: "WR", label: "WR" },
            { t: "TE", label: "TE" },
            { t: "route", label: "Route" },
          ].map(b => (
            <button key={b.t} type="button" onClick={() => { setTool(b.t); setRouteDraft(null); }}
              data-testid={`diagram-tool-${b.t}`}
              className={`text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border transition-colors ${
                tool === b.t ? "border-amber bg-amber text-[#0B0D12]" : "border-surface-3 text-slate-300 hover:border-amber"
              }`}>
              {b.label}
            </button>
          ))}
          <div className="flex-1" />
          {routeDraft && (
            <button type="button" onClick={finishRoute} className="text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border border-amber text-amber hover:bg-amber hover:text-[#0B0D12]">
              Finish Route ({routeDraft.points.length} pt)
            </button>
          )}
          <button type="button" onClick={undo} className="text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border border-surface-3 text-slate-300 hover:border-amber flex items-center gap-1">
            <Undo2 className="w-3 h-3" /> Undo
          </button>
          <button type="button" onClick={clear} className="text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border border-surface-3 text-slate-300 hover:border-red-500 hover:text-red-400 flex items-center gap-1">
            <X className="w-3 h-3" /> Clear
          </button>
        </div>
        {/* Custom-label slots — user names their own markers (e.g. MIKE, SAM, WILL) */}
        <div className="flex items-center flex-wrap gap-2 px-3 py-2 border-b border-surface-3 bg-background/50">
          <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500 mr-1">Custom Labels:</div>
          {[0, 1, 2].map(idx => {
            const label = (doc.customLabels?.[idx] || "").toUpperCase();
            const displayLabel = label || `Slot ${idx + 1}`;
            const toolId = `CUSTOM:${idx}`;
            return (
              <div key={idx} className="flex items-center gap-1" data-testid={`diagram-custom-slot-${idx}`}>
                <input
                  type="text"
                  maxLength={6}
                  value={doc.customLabels?.[idx] || ""}
                  onChange={(e) => setCustomLabel(idx, e.target.value)}
                  placeholder={`Slot ${idx + 1}`}
                  data-testid={`diagram-custom-input-${idx}`}
                  className="bg-background border border-surface-3 text-[10px] font-condensed uppercase tracking-widest px-2 py-1 w-[80px] focus:border-amber focus:outline-none text-white"
                />
                <button
                  type="button"
                  onClick={() => { setTool(toolId); setRouteDraft(null); }}
                  data-testid={`diagram-custom-tool-${idx}`}
                  disabled={!label}
                  className={`text-[10px] font-condensed uppercase tracking-widest px-2 py-1 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                    tool === toolId ? "border-amber bg-amber text-[#0B0D12]" : "border-surface-3 text-slate-300 hover:border-amber"
                  }`}
                  title={label ? `Drop "${label}" markers` : "Type a label first"}
                >
                  {label ? `+ ${displayLabel}` : `+ Slot ${idx + 1}`}
                </button>
              </div>
            );
          })}
        </div>
        </>
      )}

      <svg
        ref={svgRef}
        viewBox="0 0 500 300"
        className="w-full bg-[#052e16] block"
        style={{aspectRatio: "5 / 3"}}
        onClick={handleSvgClick}
        onMouseMove={onSvgMouseMove}
        onMouseUp={onSvgMouseUp}
        onMouseLeave={onSvgMouseUp}
        data-testid="diagram-svg"
      >
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#FBBF24" />
          </marker>
        </defs>
        {/* Field: yard lines every 50 SVG units = 10 yards visual */}
        {YARD_LINES.map((x) => (
          <line key={`yard-${x}`} x1={x} y1="10" x2={x} y2="290" stroke="#065f46" strokeWidth="1" />
        ))}
        {/* Sidelines */}
        <line x1="10" y1="10" x2="490" y2="10" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="1.5" />
        <line x1="10" y1="290" x2="490" y2="290" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="1.5" />
        {/* Hash marks */}
        {HASH_MARKS.map((x) => (
          <line key={`h1-${x}`} x1={x} y1="118" x2={x + 4} y2="118" stroke="#ffffff" strokeOpacity="0.3" strokeWidth="1" />
        ))}
        {HASH_MARKS.map((x) => (
          <line key={`h2-${x}`} x1={x} y1="182" x2={x + 4} y2="182" stroke="#ffffff" strokeOpacity="0.3" strokeWidth="1" />
        ))}
        {/* Line of scrimmage */}
        <line x1="10" y1="150" x2="490" y2="150" stroke="#F8FAFC" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="6 4" />

        {doc.routes.map(renderRoute)}
        {renderRouteDraft()}
        {doc.positions.map(renderPos)}
      </svg>
      {!readOnly && (
        <div className="px-3 py-2 text-[10px] font-condensed uppercase tracking-widest text-slate-500 border-t border-surface-3">
          Tip: pick a marker tool then click the field. Custom slots: type a label above (e.g. "MIKE", "SAM"), then click the tool to drop that named marker. Use "Route" to draw a play — click a position, then click points to draw a path, then Finish. Click any route to delete it.
        </div>
      )}
    </div>
  );
}

// -------------------- Play Editor --------------------
function PlayEditor({ play, onSaved, onDeleted }) {
  const [p, setP] = useState(play);
  const [saving, setSaving] = useState(false);


  const set = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  const toggleSituation = (s) => {
    const cur = new Set(p.situations || []);
    cur.has(s) ? cur.delete(s) : cur.add(s);
    set("situations", Array.from(cur));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: p.name, side: p.side, formation: p.formation, personnel: p.personnel,
        situations: p.situations || [], install_date: p.install_date || null,
        assignments: p.assignments || "", notes: p.notes || "", tags: p.tags || [],
        video_url: p.video_url || "", diagram: p.diagram || null,
      };
      const { data } = p.id
        ? await api.put(`/plays/${p.id}`, payload)
        : await api.post(`/plays`, payload);
      onSaved(data);
      toast.success("Play saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!p.id) return;
    if (!window.confirm("Delete this play?")) return;
    await api.delete(`/plays/${p.id}`);
    onDeleted(p.id);
    toast.success("Play deleted");
  };

  return (
    <div className="bg-surface border border-surface-3">
      <div className="p-6 border-b border-surface-3 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Play Name</div>
          <Input value={p.name || ""} onChange={(e)=>set("name", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" data-testid="play-name" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Side</div>
          <Select value={p.side || "offense"} onValueChange={(v)=>set("side", v)}>
            <SelectTrigger className="mt-1 bg-background border-surface-3 rounded-none" data-testid="play-side"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-surface-3 rounded-none">
              {SIDES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Install Date</div>
          <Input type="date" value={p.install_date || ""} onChange={(e)=>set("install_date", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" />
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Formation</div>
          <Input value={p.formation || ""} onChange={(e)=>set("formation", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" placeholder="e.g., Shotgun Trips Right" />
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Personnel</div>
          <Input value={p.personnel || ""} onChange={(e)=>set("personnel", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" placeholder="e.g., 11 (1 RB, 1 TE, 3 WR)" />
        </div>
        <div className="md:col-span-4">
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Situational Tags</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {SITUATIONS.map(s => {
              const on = (p.situations || []).includes(s.v);
              return (
                <button key={s.v} type="button" onClick={() => toggleSituation(s.v)}
                  data-testid={`situation-${s.v}`}
                  className={`text-[10px] font-condensed uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                    on ? "border-amber bg-amber text-[#0B0D12]" : "border-surface-3 text-slate-300 hover:border-amber"
                  }`}>{s.l}</button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Diagram */}
      <div className="p-6 border-b border-surface-3">
        <div className="flex items-center justify-between mb-3">
          <div className="font-condensed uppercase tracking-widest text-sm">Play Diagram</div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Football field · draw plays</div>
        </div>
        <DiagramEditor value={p.diagram} onChange={(v) => set("diagram", v)} />
      </div>

      {/* Text fields */}
      <div className="p-6 border-b border-surface-3 space-y-4">
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Assignments</div>
          <Textarea value={p.assignments || ""} onChange={(e)=>set("assignments", e.target.value)} rows={5}
            className="mt-1 bg-background border-surface-3 rounded-none font-mono text-xs" data-testid="play-assignments"
            placeholder={"OL: down-block play-side. LG pulls...\nQB: reverse-out, hand off on 2...\nWR: stalk block."} />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Coaching Notes</div>
          <Textarea value={p.notes || ""} onChange={(e)=>set("notes", e.target.value)} rows={3}
            className="mt-1 bg-background border-surface-3 rounded-none text-sm" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Video URL (Hudl link etc.)</div>
          <Input value={p.video_url || ""} onChange={(e)=>set("video_url", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none text-sm" placeholder="https://…" />
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 flex items-center justify-between">
        <div>
          {p.id && (
            <Button variant="ghost" onClick={del} className="rounded-none hover:text-red-400 h-9 text-[11px] font-condensed uppercase tracking-widest">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          )}
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-play"
          className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save Play"}
        </Button>
      </div>
    </div>
  );
}

// -------------------- Playbook page --------------------
export default function Playbook() {
  const [plays, setPlays] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [filterSide, setFilterSide] = useState("all");
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/plays");
      setPlays(data);
      if (data.length) setActiveId(prev => prev || data[0].id);
    } catch (e) {
      console.error("Failed to load plays", e);
      toast.error("Failed to load plays. Please try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = filterSide === "all" ? plays : plays.filter(p => p.side === filterSide);
  const active = plays.find(p => p.id === activeId);
  const drafting = plays.find(p => !p.id);

  const newPlay = () => {
    const draft = {
      id: null, name: "New Play", side: "offense", formation: "", personnel: "",
      situations: [], install_date: new Date().toISOString().slice(0,10),
      assignments: "", notes: "", tags: [], video_url: "",
      diagram: { positions: [], routes: [] }
    };
    setPlays([draft, ...plays.filter(p => p.id)]);
    setActiveId(null);
  };

  const onSaved = (saved) => {
    setPlays(prev => {
      const others = prev.filter(p => p.id && p.id !== saved.id);
      return [saved, ...others];
    });
    setActiveId(saved.id);
  };

  const onDeleted = (id) => {
    const next = plays.filter(p => p.id !== id);
    setPlays(next);
    setActiveId(next[0]?.id || null);
  };

  return (
    <div data-testid="playbook-page">
      <TopBar
        title="Playbook"
        subtitle="Every play. Named, tagged, and available to the AI Coach."
        actions={
          <Button onClick={newPlay} data-testid="new-play-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-[0.25em]">
            <Plus className="w-4 h-4 mr-1" /> New Play
          </Button>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 p-8">
        <aside className="space-y-2">
          <div className="flex items-center gap-1 border border-surface-3 bg-surface p-1 mb-2">
            {[{v:"all", l:"All"}, ...SIDES.map(s => ({v: s.v, l: s.l}))].map(s => (
              <button key={s.v} onClick={() => setFilterSide(s.v)}
                data-testid={`filter-${s.v}`}
                className={`flex-1 text-[10px] font-condensed uppercase tracking-widest px-2 py-2 transition-colors ${
                  filterSide === s.v ? "bg-amber text-[#0B0D12]" : "text-slate-400 hover:text-white"
                }`}>{s.l}</button>
            ))}
          </div>

          {loading && <div className="text-sm text-slate-400">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-sm text-slate-500 italic px-3 py-4">No plays match this filter.</div>
          )}
          {filtered.map(p => {
            const side = SIDES.find(s => s.v === p.side) || SIDES[0];
            const Icon = side.icon;
            return (
              <button key={p.id} onClick={() => setActiveId(p.id)}
                data-testid={`play-item-${p.id}`}
                className={`w-full text-left px-4 py-3 border transition-colors ${
                  p.id === activeId && !drafting ? "border-amber bg-surface" : "border-surface-3 hover:border-amber/50 bg-surface"
                }`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-3 h-3 ${side.color}`} />
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">{p.side.replace("_"," ")}</div>
                </div>
                <div className="font-semibold text-sm mt-1">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.formation}</div>
                {(p.situations || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.situations.map(s => (
                      <span key={s} className="text-[9px] font-condensed uppercase tracking-widest text-amber border border-amber/40 px-1.5 py-0.5">
                        {s.replace("_"," ")}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </aside>

        <div>
          {drafting && <PlayEditor key="draft" play={drafting} onSaved={onSaved} onDeleted={onDeleted} />}
          {!drafting && active && <PlayEditor key={active.id} play={active} onSaved={onSaved} onDeleted={onDeleted} />}
          {!drafting && !active && !loading && (
            <div className="text-sm text-slate-500 italic p-12 border border-dashed border-surface-3 text-center">
              Pick a play on the left or click "New Play" to install one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
